import { router, useNavigation } from 'expo-router'
import { useEffect } from 'react'
import { View } from 'react-native'
import { StyleSheet } from 'react-native-unistyles'

import OrganizationAvatar from '@/components/navigation/OrganizationAvatar'
import Card from '@/components/shared/Card'
import Page from '@/components/shared/Page'
import UniButton from '@/components/unistyles/UniButton'
import UniText from '@/components/unistyles/UniText'
import useUserRoles from '@/hooks/useUserRoles'
import { useAppStore } from '@/store/useAppStore'
import buildOrgPath from '@/utils/orgRoute'

const OrganizationManagement = () => {
  const navigation = useNavigation()
  const { isUserAdmin, isUserOwner } = useUserRoles()

  const currentOrganization = useAppStore.use.currentOrganization().organization

  useEffect(() => {
    navigation.setOptions({
      title: 'Organization Management',
    })
  }, [navigation])

  // Redirect non-admins back to account page
  useEffect(() => {
    if (!isUserAdmin && !isUserOwner) {
      router.replace(buildOrgPath('/account'))
    }
  }, [isUserAdmin, isUserOwner])

  const handleOrganizationSettings = () => {
    router.push(buildOrgPath(`/org-settings/${currentOrganization?.id}/settings`))
  }

  const handleMyTeam = () => {
    router.push(buildOrgPath('/users'))
  }

  if (!isUserAdmin && !isUserOwner) {
    return null
  }

  return (
    <>
      <Page requiresSubscription={false} viewId="organization-management">
        {/* Current Organization Header */}
        <Card style={styles.card} title="Organization">
          <View style={styles.orgHeader}>
            <View style={styles.orgProfile}>
              <OrganizationAvatar
                logoId={currentOrganization?.logo?.id}
                name={currentOrganization?.name || ''}
                organizationId={currentOrganization?.id || ''}
                size={64}
              />
              <View style={styles.orgInfo}>
                <UniText style={styles.orgName} variant="headlineSmall">
                  {currentOrganization?.name}
                </UniText>
                <UniText style={styles.orgSubtitle} variant="bodyMedium">
                  Organization Management
                </UniText>
              </View>
            </View>
          </View>
        </Card>

        {/* Management Actions */}
        <Card style={styles.card} title="Manage">
          <View style={styles.actionsSection}>
            <View style={styles.actionItem}>
              <View style={styles.actionInfo}>
                <UniText variant="titleMedium">Organization Settings</UniText>
                <UniText style={styles.actionDescription} variant="bodyMedium">
                  Configure organization details, subscription, vaccines, and
                  medical information
                </UniText>
              </View>
              <UniButton
                mode="contained"
                onPress={handleOrganizationSettings}
                style={styles.actionButton}
              >
                Settings
              </UniButton>
            </View>

            <View style={styles.actionItem}>
              <View style={styles.actionInfo}>
                <UniText variant="titleMedium">My Team</UniText>
                <UniText style={styles.actionDescription} variant="bodyMedium">
                  Manage users, roles, and permissions for your organization
                </UniText>
              </View>
              <UniButton
                mode="contained"
                onPress={handleMyTeam}
                style={styles.actionButton}
              >
                View Team
              </UniButton>
            </View>
          </View>
        </Card>
      </Page>
    </>
  )
}

const styles = StyleSheet.create((theme) => ({
  actionButton: {
    minWidth: 100,
  },
  actionDescription: {
    color: theme.colors.onSurfaceVariant,
  },
  actionInfo: {
    flex: 1,
    gap: theme.tokens.spacing[1],
  },
  actionItem: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: theme.tokens.spacing[4],
  },
  actionsSection: {
    gap: theme.tokens.spacing[5],
  },
  card: {
    marginBottom: theme.tokens.spacing[4],
    padding: theme.tokens.spacing[4],
  },
  orgHeader: {
    gap: theme.tokens.spacing[4],
  },
  orgInfo: {
    flex: 1,
    gap: theme.tokens.spacing[1],
  },
  orgName: {
    fontWeight: '600',
  },
  orgProfile: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.tokens.spacing[4],
  },
  orgSubtitle: {
    color: theme.colors.onSurfaceVariant,
  },
}))

export default OrganizationManagement
